import React, { useEffect, useState, useContext } from 'react'
import { getGroupedAlarms } from '../api/requests'
import GroupedAlarmsTable from '../components/tables/GroupedAlarmsTable'
import { AlarmsEditModal } from '../AlarmsEditModal'
import { useIntl } from 'react-intl'
import { CustomizationContext } from '../../customization/CustomizationContext'
import { useAuth } from '../../auth/core/AuthProvider'

const GroupedAlarmsPage: React.FC = () => {
  // @ts-ignore
  const { settings } = useContext(CustomizationContext)
  const [alarmsData, setAlarmsData] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedAlarmId, setSelectedAlarmId] = useState<string | null>(null)
  const [refreshFunction, setRefreshFunction] = useState<Function>(() => {})
  const intl = useIntl()
  const { user } = useAuth()
  const canEdit = user?.permissions?.canManageSiteData ?? false

  const fetchAlarms = async (
    page: number = 1,
    filters?: {
      startDate?: Date
      endDate?: Date
      siteAcknowledge?: boolean
      companyId?: number
      alertType?: 'hourly' | 'daily'
    }
  ) => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await getGroupedAlarms(
        page,
        10,
        filters?.startDate,
        filters?.endDate,
        filters?.siteAcknowledge,
        filters?.companyId,
        filters?.alertType
      )
      setAlarmsData(response)
      setCurrentPage(page)
    } catch (error) {
      console.error('Error fetching alarms:', error)
      setError('Failed to load alarms. Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    const userAlertType = settings?.alertType || 'daily'
    const userCompanyId = settings?.companyS ? parseInt(settings.companyS) : undefined

    const filters: {
      startDate?: Date
      endDate?: Date
      siteAcknowledge?: boolean
      companyId?: number
      alertType?: 'hourly' | 'daily'
    } = {
      alertType: userAlertType,
    }

    if (userCompanyId) {
      filters.companyId = userCompanyId
    }

    fetchAlarms(1, filters)
  }, [settings?.alertType, settings?.companyS]) // Run when settings change

  const handleEditAlarm = (alarmId: number, fetchData: (page: number) => void) => {
    setSelectedAlarmId(alarmId.toString())
    setRefreshFunction(() => fetchData)
  }

  return (
    <>
      <div className='card mb-5 mb-xl-8 shadow-sm'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>
              {intl.formatMessage({ id: 'ALARMS.TITLE' })}{' '}
              {settings?.alertType && settings?.alertType !== 'both' && (
                <span className='badge badge-light-primary fw-normal fs-7'>
                  {settings?.alertType === 'daily'
                    ? intl.formatMessage({ id: 'ALARMS.DAILY' }) || 'Daily'
                    : intl.formatMessage({ id: 'ALARMS.HALFHOURLY' }) || 'Half Hourly'}
                </span>
              )}
            </span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              View alarms grouped by date, company and site
            </span>
          </h3>
          <div className='card-toolbar'></div>
        </div>
        <div className='card-body py-3'>
          {isLoading && !alarmsData ? (
            <div className='d-flex justify-content-center p-5'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className='alert alert-danger'>
              <div className='d-flex align-items-center'>
                <i className='fa fa-exclamation-triangle fs-2hx text-danger me-4'></i>
                <div className='d-flex flex-column'>
                  <h4 className='mb-1 text-dark'>Error</h4>
                  <span>{error}</span>
                </div>
              </div>
            </div>
          ) : (
            <GroupedAlarmsTable
              data={alarmsData}
              title='Grouped Alarms'
              fetchData={fetchAlarms}
              editCallback={handleEditAlarm}
            />
          )}
        </div>
      </div>

      {selectedAlarmId && (
        <AlarmsEditModal
          show={!!selectedAlarmId}
          itemIdForUpdate={selectedAlarmId}
          canEdit={canEdit}
          onHide={() => setSelectedAlarmId(null)}
          refreshFunction={(itemId: string) => {
            if (refreshFunction) {
              refreshFunction(currentPage)
            }
          }}
        />
      )}
    </>
  )
}

export default GroupedAlarmsPage
